import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  AppTheme, 
  DEFAULT_THEME, 
  THEME_BACKGROUNDS, 
  THEME_ACCENTS, 
  THEME_EDGES, 
  THEME_FONTS 
} from '@/types/evolve';

const THEME_STORAGE_KEY = 'evolve-app-theme';

export function useTheme(userId?: string) {
  const [theme, setTheme] = useState<AppTheme>(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return DEFAULT_THEME;
      }
    }
    return DEFAULT_THEME;
  });
  const [loaded, setLoaded] = useState(false);

  // Load theme from Supabase on mount
  useEffect(() => {
    const loadTheme = async () => {
      if (!userId) {
        setLoaded(true);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('theme_preferences')
          .eq('id', userId)
          .single();

        if (!error && data?.theme_preferences) {
          const dbTheme = data.theme_preferences as unknown as AppTheme;
          if (dbTheme && dbTheme.background && dbTheme.accent) {
            setTheme(dbTheme);
            localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(dbTheme));
          }
        }
      } catch (error) {
        console.error('Error loading theme:', error);
      } finally {
        setLoaded(true);
      }
    };

    loadTheme();
  }, [userId]);

  // Apply theme to CSS variables
  const applyTheme = useCallback((newTheme: AppTheme) => {
    const root = document.documentElement;
    
    // Background
    const bg = THEME_BACKGROUNDS.find(b => b.value === newTheme.background);
    if (bg) {
      root.style.setProperty('--background', bg.hsl);
      root.style.setProperty('--evolve-black', bg.hsl);
      // Also set card/popover to match background tone
      const [h, s, l] = bg.hsl.split(' ');
      const lightness = parseFloat(l) + 2;
      root.style.setProperty('--card', `${h} ${s} ${lightness}%`);
      root.style.setProperty('--popover', `${h} ${s} ${lightness}%`);
    }
    
    // Accent
    const accent = THEME_ACCENTS.find(a => a.value === newTheme.accent);
    if (accent) {
      root.style.setProperty('--primary', accent.hsl);
      root.style.setProperty('--accent', accent.hsl);
      root.style.setProperty('--ring', accent.hsl);
      root.style.setProperty('--evolve-red', accent.hsl);
    }
    
    // Edge style
    const edge = THEME_EDGES.find(e => e.value === newTheme.edge);
    if (edge) {
      root.style.setProperty('--radius', edge.radius);
    }
    
    // Font
    const font = THEME_FONTS.find(f => f.value === newTheme.font);
    if (font) {
      root.style.setProperty('--font-display', font.display);
      root.style.setProperty('--font-body', font.body);
      
      // Apply directly to elements
      document.body.style.fontFamily = `${font.body}, sans-serif`;
      document.querySelectorAll('h1, h2, h3, h4, h5, h6, .font-display').forEach((el) => {
        (el as HTMLElement).style.fontFamily = `${font.display}, sans-serif`;
      });
    }
  }, []);

  // Apply theme on mount and changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  // Update theme (save to both localStorage and Supabase)
  const updateTheme = useCallback(async (updates: Partial<AppTheme>) => {
    setTheme(prev => {
      const newTheme = { ...prev, ...updates };
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(newTheme));
      
      // Save to Supabase if logged in
      if (userId) {
        supabase
          .from('profiles')
          .update({ theme_preferences: JSON.parse(JSON.stringify(newTheme)) })
          .eq('id', userId)
          .then(({ error }) => {
            if (error) console.error('Error saving theme:', error);
          });
      }
      
      return newTheme;
    });
  }, [userId]);

  // Reset to default
  const resetTheme = useCallback(async () => {
    setTheme(DEFAULT_THEME);
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(DEFAULT_THEME));
    
    if (userId) {
      await supabase
        .from('profiles')
        .update({ theme_preferences: JSON.parse(JSON.stringify(DEFAULT_THEME)) })
        .eq('id', userId);
    }
  }, [userId]);

  return {
    theme,
    updateTheme,
    resetTheme,
    loaded,
  };
}
